import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import './RiwayatPembayaran.css';
import {
  FiCreditCard,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiDownload,
  FiCalendar,
  FiFilter,
  FiDollarSign,
  FiFileText,
  FiInfo
} from 'react-icons/fi';

const RiwayatPembayaran = () => {
  const { user } = useAuth();
  const [pembayaranList, setPembayaranList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [stats, setStats] = useState({
    totalPembayaran: 0,
    totalDibayar: 0,
    jumlahTransaksi: 0,
    pending: 0
  });

  const fetchPembayaran = useCallback(async () => {
    if (!user?.nim) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/pembayaran?nim=${user.nim}`, {
        params: {
          status: filterStatus !== 'all' ? filterStatus : undefined,
          startDate: dateRange.start || undefined,
          endDate: dateRange.end || undefined
        }
      });

      const data = response.data.data || [];
      setPembayaranList(data);

      // Calculate statistics
      const totalDibayar = data
        .filter(p => p.status === 'berhasil')
        .reduce((sum, p) => sum + p.jumlah, 0);
      
      const pending = data.filter(p => p.status === 'pending').length;

      setStats({
        totalPembayaran: data.length,
        totalDibayar,
        jumlahTransaksi: data.filter(p => p.status === 'berhasil').length,
        pending
      });
    } catch (error) {
      console.error('Error fetching pembayaran:', error);
      toast.error('Gagal memuat riwayat pembayaran');
    } finally {
      setLoading(false);
    }
  }, [user, filterStatus, dateRange]);

  useEffect(() => {
    fetchPembayaran();
  }, [fetchPembayaran]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      berhasil: {
        label: 'Berhasil',
        icon: <FiCheckCircle />,
        className: 'success'
      },
      pending: {
        label: 'Pending',
        icon: <FiClock />,
        className: 'pending'
      },
      gagal: {
        label: 'Gagal',
        icon: <FiXCircle />,
        className: 'failed'
      }
    };
    return statusMap[status] || statusMap.pending;
  };

  const handleDownloadBukti = async (id) => {
    try {
      const response = await axios.get(`/api/pembayaran/${id}/bukti`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bukti-pembayaran-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Bukti pembayaran berhasil diunduh');
    } catch (error) {
      console.error('Error downloading bukti:', error);
      toast.error('Gagal mengunduh bukti pembayaran');
    }
  };

  const handleResetFilter = () => {
    setFilterStatus('all');
    setDateRange({ start: '', end: '' });
  };

  if (loading) {
    return (
      <div className="riwayat-pembayaran-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Memuat riwayat pembayaran...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="riwayat-pembayaran-container">
      {/* Header */}
      <div className="riwayat-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="icon-wrapper">
              <FiCreditCard className="header-icon" />
            </div>
            <div>
              <h1 className="header-title">Riwayat Pembayaran</h1>
              <p className="header-subtitle">
                Lihat semua transaksi pembayaran Anda
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FiFileText />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Transaksi</p>
            <h3 className="stat-value">{stats.totalPembayaran}</h3>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Dibayar</p>
            <h3 className="stat-value">{formatCurrency(stats.totalDibayar)}</h3>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-details">
            <p className="stat-label">Pembayaran Berhasil</p>
            <h3 className="stat-value">{stats.jumlahTransaksi}</h3>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <FiClock />
          </div>
          <div className="stat-details">
            <p className="stat-label">Menunggu Konfirmasi</p>
            <h3 className="stat-value">{stats.pending}</h3>
          </div>
        </div>
      </div>

      {/* Filter Card */}
      <div className="filter-card">
        <div className="filter-header">
          <FiFilter />
          <h3>Filter Riwayat</h3>
        </div>

        <div className="filter-content">
          {/* Status Filter */}
          <div className="filter-section">
            <label className="filter-label">Status Pembayaran:</label>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                Semua
              </button>
              <button
                className={`filter-btn ${filterStatus === 'berhasil' ? 'active' : ''}`}
                onClick={() => setFilterStatus('berhasil')}
              >
                Berhasil
              </button>
              <button
                className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
                onClick={() => setFilterStatus('pending')}
              >
                Pending
              </button>
              <button
                className={`filter-btn ${filterStatus === 'gagal' ? 'active' : ''}`}
                onClick={() => setFilterStatus('gagal')}
              >
                Gagal
              </button>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="filter-section">
            <label className="filter-label">Periode:</label>
            <div className="date-range-inputs">
              <div className="date-input-group">
                <FiCalendar />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="date-input"
                />
              </div>
              <span className="date-separator">sampai</span>
              <div className="date-input-group">
                <FiCalendar />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="date-input"
                />
              </div>
              <button className="btn-reset" onClick={handleResetFilter}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pembayaran List */}
      <div className="pembayaran-list-card">
        <div className="card-header">
          <h3>
            <FiFileText />
            Daftar Transaksi
          </h3>
          <span className="table-count">
            {pembayaranList.length} Transaksi
          </span>
        </div>

        {pembayaranList.length === 0 ? (
          <div className="empty-state">
            <FiFileText size={64} />
            <p>Tidak ada riwayat pembayaran</p>
            <small>
              {filterStatus !== 'all' || dateRange.start || dateRange.end
                ? 'Coba ubah filter pencarian'
                : 'Belum ada transaksi pembayaran yang tercatat'}
            </small>
          </div>
        ) : (
          <div className="pembayaran-timeline">
            {pembayaranList.map((pembayaran) => {
              const statusInfo = getStatusInfo(pembayaran.status);
              return (
                <div key={pembayaran._id} className="timeline-item">
                  <div className={`timeline-marker ${statusInfo.className}`}>
                    {statusInfo.icon}
                  </div>
                  
                  <div className="timeline-content">
                    <div className="pembayaran-card">
                      <div className="pembayaran-header">
                        <div className="pembayaran-info">
                          <h4 className="pembayaran-title">
                            {pembayaran.jenis_pembayaran}
                          </h4>
                          <p className="pembayaran-desc">
                            {pembayaran.keterangan || 'Pembayaran tagihan'}
                          </p>
                        </div>
                        <div className={`status-badge ${statusInfo.className}`}>
                          {statusInfo.icon}
                          <span>{statusInfo.label}</span>
                        </div>
                      </div>

                      <div className="pembayaran-details">
                        <div className="detail-row">
                          <div className="detail-item">
                            <FiDollarSign className="detail-icon" />
                            <div>
                              <span className="detail-label">Jumlah</span>
                              <span className="detail-value">
                                {formatCurrency(pembayaran.jumlah)}
                              </span>
                            </div>
                          </div>

                          <div className="detail-item">
                            <FiCalendar className="detail-icon" />
                            <div>
                              <span className="detail-label">Tanggal</span>
                              <span className="detail-value">
                                {formatDate(pembayaran.tanggal_bayar)}
                              </span>
                            </div>
                          </div>

                          <div className="detail-item">
                            <FiClock className="detail-icon" />
                            <div>
                              <span className="detail-label">Waktu</span>
                              <span className="detail-value">
                                {formatTime(pembayaran.tanggal_bayar)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {pembayaran.metode_pembayaran && (
                          <div className="payment-method">
                            <FiCreditCard />
                            <span>
                              {pembayaran.metode_pembayaran}
                              {pembayaran.nomor_referensi && 
                                ` - Ref: ${pembayaran.nomor_referensi}`
                              }
                            </span>
                          </div>
                        )}
                      </div>

                      {pembayaran.status === 'berhasil' && (
                        <div className="pembayaran-actions">
                          <button
                            className="btn-download"
                            onClick={() => handleDownloadBukti(pembayaran._id)}
                          >
                            <FiDownload />
                            Unduh Bukti
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="info-box">
        <FiInfo />
        <div>
          <strong>Informasi Penting:</strong>
          <ul>
            <li>Simpan bukti pembayaran untuk keperluan administrasi</li>
            <li>Status "Pending" akan diproses maksimal 2x24 jam</li>
            <li>Jika ada masalah pembayaran, hubungi bagian keuangan</li>
            <li>Bukti pembayaran dapat diunduh setelah pembayaran berhasil</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RiwayatPembayaran;
